import { FieldArray } from 'formik';
import { DateTime } from 'luxon';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { array, number, object, string } from 'yup';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Cabecalho from 'Component/Cabecalho';
import Form, {
  Botao as BotaoForm,
  Campo,
  Contexto,
  Selecao,
} from 'Component/Form';
import Icone from 'Component/Icone';
import LayoutDefault from 'Component/Layout';
import Holerite from 'Interface/Holerites';
import Item from 'Interface/Itens';
import { ANOS, MESES, TIPOS_HOLERITE } from 'View/constants';
import {
  useEditarHoleriteMutation,
  useRecuperarHoleriteQuery,
} from 'View/Service/Default/Holerites';
import { useRemoverItemMutation } from 'View/Service/Default/Itens';
import { useRecuperarVinculosEmpregaticiosQuery } from 'View/Service/Default/VinculosEmpregaticios';

const regras = object({
  vinculo_empregaticio_id: number().required('Obrigatório'),
  ano: number().required('Obrigatório'),
  mes: number().required('Obrigatório'),
  tipo: number().required('Obrigatório'),
  salario_base: number().required('Obrigatório'),
  base_calculo_fgts: number().required('Obrigatório'),
  fgts_mes: number().required('Obrigatório'),
  base_calculo_inss: number().required('Obrigatório'),
  base_calculo_irrf: number().required('Obrigatório'),
  total_de_vencimentos: number().required('Obrigatório'),
  total_de_descontos: number().required('Obrigatório'),
  liquido: number().required('Obrigatório'),
  faixa_irrf: number().required('Obrigatório'),
  itens: array().of(
    object().shape({
      codigo: number().required('Obrigatório'),
      descricao: string().required('Obrigatório'),
      referencia: string().required('Obrigatório'),
      vencimentos: number().required('Obrigatório'),
      descontos: number().required('Obrigatório'),
    })
  ),
});

const Editar = (): React.ReactElement => {
  const navegacao = useNavigate();
  const toast = useToast();
  const { usuarioId = 0, id = 0 } = useParams();

  const [editarHolerite] = useEditarHoleriteMutation();
  const [removerItem] = useRemoverItemMutation();

  const { data: { vinculos } = {} } = useRecuperarVinculosEmpregaticiosQuery({
    finder: 'listaJson',
    contain: ['Empresas'],
    options: { usuario_id: usuarioId },
  });

  const { data: { holerite } = {} } = useRecuperarHoleriteQuery({
    id,
    contain: ['Itens'],
  });

  const enviar = async (dados: Holerite): Promise<void> => {
    try {
      await editarHolerite({
        ...dados,
        options: { contain: ['Itens'] },
      }).unwrap();

      toast({ conteudo: 'Editado com sucesso!', tema: 'success' });

      navegacao(`/usuarios/editar/${usuarioId}`);
    } catch (error) {
      toast({
        titulo: 'Erro ao editar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const remover = async (item: Item): Promise<void> => {
    try {
      await removerItem(item).unwrap();

      toast({ conteudo: 'Removido com sucesso!', tema: 'success' });
    } catch (error) {
      toast({
        titulo: 'Erro ao remover',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Editando holerite"
        descricao="Preencha o formulário e clique em enviar para editar o holerite"
      />
      <Form onSubmit={enviar} regras={regras} inicial={holerite}>
        <div className="row justify-content-center">
          <div className="col-md-12">
            <div className="bg-white shadow-sm rounded p-4">
              <div className="row">
                <div className="col-md-12">
                  <Selecao
                    name="vinculo_empregaticio_id"
                    rotulo="Vinculo empregatício"
                    opcoes={vinculos}
                    autoSelecao
                  />
                </div>
                <div className="col-md-4">
                  <Selecao name="tipo" rotulo="Tipo" opcoes={TIPOS_HOLERITE} />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="mes"
                    rotulo="Mês"
                    opcoes={MESES}
                    inicial={DateTime.now().month}
                  />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="ano"
                    rotulo="Ano"
                    opcoes={ANOS}
                    inicial={DateTime.now().year}
                  />
                </div>
                <div className="col-md-12 border-top border-bottom my-3 pt-3">
                  <Contexto>
                    {({ values }) => (
                      <FieldArray name="itens">
                        {({ remove, push }) => (
                          <React.Fragment>
                            {values?.itens.length > 0 &&
                              values?.itens?.map(
                                (item: Item, indice: number) => (
                                  <div
                                    key={indice.toString()}
                                    className={`row ${
                                      indice && 'border-top pt-3'
                                    }`}
                                  >
                                    <div className="col-md-11">
                                      <div className="row">
                                        <div className="col-md-1">
                                          <Campo
                                            name={`itens.${indice}.codigo`}
                                            rotulo="Código"
                                          />
                                        </div>
                                        <div className="col-md-5">
                                          <Campo
                                            name={`itens.${indice}.descricao`}
                                            rotulo="Descrição"
                                          />
                                        </div>
                                        <div className="col-md-2">
                                          <Campo
                                            name={`itens.${indice}.referencia`}
                                            rotulo="Referência"
                                          />
                                        </div>
                                        <div className="col-md-2">
                                          <Campo
                                            name={`itens.${indice}.vencimentos`}
                                            rotulo="Vencimentos"
                                          />
                                        </div>
                                        <div className="col-md-2">
                                          <Campo
                                            name={`itens.${indice}.descontos`}
                                            rotulo="Descontos"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-1">
                                      <Botao
                                        tema="danger"
                                        icone="trash-fill"
                                        onClick={() =>
                                          item.id
                                            ? remover(item)
                                            : remove(indice)
                                        }
                                        confirmacao="Tem certeza que deseja remover esse item?"
                                      />
                                    </div>
                                  </div>
                                )
                              )}
                            <div className="col-md-12 mb-3 d-flex justify-content-center">
                              <Botao
                                tema="success"
                                icone="plus"
                                onClick={() =>
                                  push({
                                    codigo: '',
                                    descricao: '',
                                    referencia: '',
                                    vencimentos: '',
                                    descontos: '',
                                  })
                                }
                              >
                                Adicionar item
                              </Botao>
                            </div>
                          </React.Fragment>
                        )}
                      </FieldArray>
                    )}
                  </Contexto>
                </div>
                <div className="col-md-4">
                  <Campo
                    name="salario_base"
                    rotulo="Salário base"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="base_calculo_fgts"
                    rotulo="Base calc. FGTS"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="fgts_mes"
                    rotulo="FGTS do mês"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="base_calculo_inss"
                    rotulo="Base calc. INSS"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="base_calculo_irrf"
                    rotulo="Base calc. IRRF"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="total_de_vencimentos"
                    rotulo="Total de vencimentos"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="total_de_descontos"
                    rotulo="Total de descontos"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="liquido"
                    rotulo="Valor líquido"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo name="faixa_irrf" rotulo="Faixa IRRF" />
                </div>
              </div>

              <div className="mt-5">
                <BotaoForm>Enviar</BotaoForm>
                <BotaoLink
                  tema="light"
                  to={`/usuarios/editar/${usuarioId}`}
                  className="ms-2"
                >
                  Cancelar
                </BotaoLink>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </LayoutDefault>
  );
};

export default Editar;
